import { CITY } from '@city'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useSchema } from '../components/useSchema'
import type { LineOpenSource } from '../state/metroMapStore'
import { useMetroMapDispatch } from '../state/useMetroMapStore'
import { lineDiagramFor } from './lineDiagramFor'
import { LineUrlKey } from './LineUrlKey'
import { lineTitleParts } from './lineTitleParts'

export interface LineLinks {
  /** `/<locale>/lines/<slug>` of a line that has a line view here, else `undefined`. */
  readonly hrefFor: (lineId: string) => string | undefined
  /** `/<locale>` of the page — the prefix every in-app link carries. */
  readonly localePath: string
  /** The link text: "Bakerloo line", "DLR" (Figma LineView E). */
  readonly titleFor: (lineId: string) => string | undefined
  /** Opens the line view in place (the link's in-app click). */
  readonly open: (lineId: string, source: LineOpenSource) => void
}

/**
 * The station card's and StationDetails' line link (Figma LineView E):
 * where it points and what a click does. Lines without a diagram in this
 * city get no link.
 */
export function useLineLinks(): LineLinks {
  const schema = useSchema()
  const dispatch = useMetroMapDispatch()
  const { locale } = useParams<{ locale?: string }>()
  const { t } = useTranslation('line-details')
  return useMemo(() => {
    const hasView = (lineId: string): boolean =>
      schema !== null && lineDiagramFor(schema, lineId, CITY.lineDiagrams) !== null
    return {
      localePath: locale === undefined ? '' : `/${locale}`,
      hrefFor: (lineId) =>
        schema === null || !hasView(lineId)
          ? undefined
          : `${locale === undefined ? '' : `/${locale}`}/lines/${LineUrlKey.of(schema, lineId)}`,
      titleFor: (lineId) => {
        const line = schema?.stations.find((s) => s.line.id === lineId)?.line
        if (line === undefined) return undefined
        const { name, takesSuffix } = lineTitleParts(line, CITY.lineTitles)
        return takesSuffix ? t('line_title', { name }) : name
      },
      open: (lineId, source) => {
        if (schema === null || !hasView(lineId)) return
        dispatch({
          kind: 'LineSelected',
          line: { lineId, slug: LineUrlKey.of(schema, lineId) },
          source,
        })
      },
    }
  }, [schema, dispatch, locale, t])
}
